import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import moment from 'moment';
import { Divider } from 'antd';
import { useTheme } from 'styled-components';

import {
  createTicket,
  getReasons,
  getServiceChannels,
  getTicketJourneys,
  getTicketProducts,
  getTicketResolutionReasons,
  getTicketStatus,
  getTicketTeams,
  updateTicket,
  createTicketSupportService,
  createTicketFiles,
  selectTicket,
} from '~/store/tickets/actions';

import { Calendar } from '~/ui/assets/icons';
import { Alert, Button, Flex, RenderRichText, withStepperProps } from '~/ui/components';
import { ChildrenDrawer, TicketData } from '~/components';

import { uppercaseFirst } from '~/common';
import { findNamesByIds, createSelectValues, findLastNameById, findNameById } from '~/common/selectValues';
import { DrawerText } from '~/components/ModalDrawer/ModalDrawer.styled';
import { TicketOriginNames } from '~/typings/enums/Ticket/TicketOrigin';
import { themeToggleValue } from '~/common/utils/theme';
import { useDrawer } from '~/hooks/useDrawer';

import { TableNameTypes, useTracking } from '~/hooks/useTracking';

import { getPersonDetail } from '~/store/people/actions';
import TicketDetails from '~/components/Ticket/drawers/TicketDetails/TicketDetails';
import { Container, Title, Text, TagText, DateTimeViewer, ContractList, InfoContainer } from './TicketResume.styled';

type TicketResumeProps = {
  data?: TicketData;
  buttonText?: string;
  isUpdating?: boolean;
};

export const AWAITING_RESOLUTION = 63;

export const TicketResume = withStepperProps<TicketResumeProps>(({ stepper, data, buttonText, isUpdating = false }) => {
  const { setConfig, closeDrawer, setLoading, openDrawer } = useDrawer();
  const theme = useTheme();
  const { handleTracking } = useTracking();
  const personControl = useAwaitControl(getPersonDetail);
  const personDetails = personControl.result();

  const createTicketControl = useAwaitControl(createTicket);
  const getReasonsControl = useAwaitControl(getReasons);
  const getServiceChannelsControl = useAwaitControl(getServiceChannels);
  const getTeamsControl = useAwaitControl(getTicketTeams);
  const updateTicketControl = useAwaitControl(updateTicket);
  const getJourneysControl = useAwaitControl(getTicketJourneys);
  const getProductsControl = useAwaitControl(getTicketProducts);
  const getTicketResolutionReasonsControl = useAwaitControl(getTicketResolutionReasons);
  const getTicketStatusControl = useAwaitControl(getTicketStatus);
  const selectTicketControl = useAwaitControl(selectTicket);

  const channels = getServiceChannelsControl.result();
  const reasons = getReasonsControl.result();
  const teams = getTeamsControl.result();
  const journeys = getJourneysControl.result();
  const products = getProductsControl.result();
  const resolutionReasons = getTicketResolutionReasonsControl.result();
  const status = getTicketStatusControl.result();
  const createdTicket = createTicketControl.result();

  const createTicketLoading = createTicketControl.isRunning();
  const createTicketSuccess = createTicketControl.isSuccessful();
  const createTicketFailure = createTicketControl.hasFailure();

  const createTicketSupportServiceControl = useAwaitControl(createTicketSupportService);
  const createTicketSupportServiceLoading = createTicketSupportServiceControl.isRunning();
  const createTicketSupportServiceSuccess = createTicketSupportServiceControl.isSuccessful();

  const createTicketFilesControl = useAwaitControl(createTicketFiles);
  const createTicketFilesSuccess = createTicketFilesControl.isSuccessful();
  const createTicketFilesFailure = createTicketFilesControl.hasFailure();
  const createTicketFilesLoading = createTicketFilesControl.isRunning();

  const isUpdatingTicket = updateTicketControl.isRunning();
  const ticketSuccessfullyUpdated = updateTicketControl.isSuccessful();
  const hasSubmitted = useRef(false);

  const alreadyExistsTicket = useMemo(
    () => createTicketFailure && !!createdTicket?.ticketId,
    [createTicketFailure, createdTicket],
  );

  const handleCreateTicket = () => {
    createTicketControl.start(data);

    if (data?.ticket?.companyId) return;

    const event = {
      event_name: 'concluiu_abrir_ticket',
      titulo_tela: 'PersonDetailsNew',
      cliente_person_id: personDetails?.id,
      cliente_convenio: personDetails?.agreementName,
      cliente_oportunidade: personDetails?.isOpportunity,
    };

    handleTracking(event, TableNameTypes.BACKOFFICE_PEOPLE_TICKETS);
  };

  const handleCancel = () => {
    closeDrawer('ticket-modal');
  };

  const handleRedirectToTicket = (id: string) => {
    closeDrawer('ticket-modal');
    selectTicketControl.start(id);

    openDrawer('ticket-details', <TicketDetails id={id} />, {
      title: 'Detalhes do ticket',
      closable: true,
      width: 900,
      sideBySide: true,
    });
  };

  const newFiles = useMemo(() => (data?.files || []).filter((item: any) => item?.file instanceof File), [data?.files]);

  const updateOrCreateTicket = () => {
    if (isUpdating) {
      const ticketData = {
        ticketStatusId: data?.ticket.ticketStatusId,
        resolvingAreaId: null,
        priorityTicket: data?.ticket.priorityTicket ?? 'WAITING_DEFINITION',
        ticketReasonId: data?.ticket.ticketReasonId,
        serviceChannelId: data?.ticket.serviceChannelId,
        userCreated: data?.ticket.userCreated,
        userCreatedId: data?.ticket.userCreatedId,
        teamId: data?.ticket.teamId,
        ...(data?.contractsChanged ? { contracts: data?.ticket.contracts } : {}),
        journeyId: data?.ticket.journeyId,
        contactOrigin: data?.ticket.contactOrigin,
        personId: data?.ticket.personId,
        product: data?.ticket.product,
        description: data?.ticket.description,
        justificationResolutionId: data?.ticket.justificationResolutionId,
        files: newFiles,
      };

      hasSubmitted.current = true;
      updateTicketControl.start({
        ticketId: data?.ticket?.id,
        data: ticketData,
        personId: data?.ticket?.personId,
      });
    } else {
      handleCreateTicket();
    }
  };

  const renderNames = useCallback((list, id: number) => {
    const ids = createSelectValues(list, id);
    const names = findNamesByIds(list, ids);
    return names.join(' - ');
  }, []);

  const justificationResolution = useMemo(
    () => resolutionReasons?.find((reason) => reason.id === data?.ticket?.justificationResolutionId)?.justification,
    [resolutionReasons, data?.ticket?.justificationResolutionId],
  );

  const getAlertMessage = useMemo(
    () =>
      data?.ticket?.ticketStatusId === AWAITING_RESOLUTION ? (
        <>
          Seu ticket será encaminhado para a área <b>responsável</b>
        </>
      ) : (
        <>
          Esse ticket é considerado como um <b>atendimento concluído</b>, servindo para documentação do contato.
        </>
      ),
    [data?.ticket?.ticketStatusId],
  );

  useEffect(() => {
    setLoading(
      'ticket-modal',
      createTicketLoading || isUpdatingTicket || createTicketSupportServiceLoading || createTicketFilesLoading,
    );
  }, [createTicketLoading, isUpdatingTicket, createTicketSupportServiceLoading, createTicketFilesLoading]);

  useEffect(() => {
    if (createTicketSuccess) {
      if (createdTicket?.id) {
        createTicketSupportServiceControl.start({
          id: createdTicket?.id,
          data: {
            ...data?.ticket,
            createdByUser: data?.ticket.userCreated,
            createdByUserId: data?.ticket?.userCreatedId,
          },
        });

        if (data.files) {
          createTicketFilesControl.start({
            ticketId: createdTicket?.id,
            files: data.files,
            createdBy: data?.ticket.userCreated,
            createdById: data?.ticket?.userCreatedId,
            personId: data?.ticket?.personId,
          });
        }
      } else {
        closeDrawer('ticket-modal');
      }
    }
  }, [createTicketSuccess]);

  useEffect(() => {
    if (isUpdating) {
      if (ticketSuccessfullyUpdated && hasSubmitted.current) {
        closeDrawer('ticket-modal');
      }
      return;
    }

    if (data.files) {
      if (createTicketFilesSuccess || createTicketFilesFailure) {
        closeDrawer('ticket-modal');
      }
    } else if (createTicketSupportServiceSuccess) {
      closeDrawer('ticket-modal');
    }
  }, [
    isUpdating,
    ticketSuccessfullyUpdated,
    createTicketSupportServiceSuccess,
    createTicketFilesSuccess,
    createTicketFilesFailure,
  ]);

  useEffect(() => {
    setConfig('ticket-modal', {
      title: 'Revise suas informações',
      onBackClick: stepper.prevStep,
      backButton: true,
      maskClosable: false,
    });

    return () => {
      createTicketControl.clear();
      createTicketSupportServiceControl.clear();
      createTicketFilesControl.clear();
      updateTicketControl.clear();
    };
  }, []);

  return (
    <>
      <Container data-testid="ticket-resume-wrapper">
        {isUpdating && (
          <Text>
            Verifique se as informações abaixo estão corretas para prosseguir com a atualização do ticket ou volte para
            revisa-lás.
          </Text>
        )}

        {!isUpdating && <Alert label={getAlertMessage} status="warning" />}

        {!!data?.ticket?.ticketScheduleDate && (
          <DateTimeViewer>
            <Calendar />
            {uppercaseFirst(moment(data?.ticket?.ticketScheduleDate).format('dddd, D [de] MMMM'))}
            <Divider type="vertical" />
            <span>{moment(data?.ticket?.ticketScheduleDate).format('HH:mm')}</span>
          </DateTimeViewer>
        )}

        <InfoContainer>
          <div>
            <Title>Agente</Title>
            <Text>{data?.ticket?.userCreated}</Text>
          </div>

          <div>
            <Title>Produto</Title>
            <TagText>
              {findNameById(products, data?.ticket?.product, {
                value: 'name',
                label: 'description',
              })}
            </TagText>
          </div>

          <div>
            <Title>Etapa da jornada</Title>
            <Text>{renderNames(journeys, data?.ticket?.journeyId)}</Text>
          </div>

          <div>
            <Title>Motivo da abertura</Title>
            <Text>{findLastNameById(reasons, data?.ticket?.ticketReasonId)}</Text>
          </div>

          <div>
            <Title>Canal</Title>
            <Text>{renderNames(channels, data?.ticket?.serviceChannelId)}</Text>
          </div>

          <div>
            <Title>Origem do contato</Title>
            <Text>{TicketOriginNames[data?.ticket?.contactOrigin]}</Text>
          </div>

          <div>
            <Title>Time</Title>
            <Text>{renderNames(teams, data?.ticket?.teamId)}</Text>
          </div>

          {!!data?.ticket?.category && (
            <div>
              <Title>Categoria</Title>
              <Text>{data?.ticket?.category?.description}</Text>
            </div>
          )}

          <div>
            <Title>Status de resolução</Title>
            <Text>{renderNames(status, data?.ticket?.ticketStatusId)}</Text>
          </div>

          {!!data?.ticket?.contracts?.length && (
            <div>
              <Title>Contratos</Title>
              <ContractList>
                {data?.ticket?.contracts.map(({ id }) => (
                  <TagText key={id}>{id}</TagText>
                ))}
              </ContractList>
            </div>
          )}

          {!!data?.ticket?.justificationResolutionId && (
            <div>
              <Title>Justificativa da resolução</Title>
              <Text>{justificationResolution}</Text>
            </div>
          )}

          {!!data?.files?.length && (
            <div>
              <Title>Arquivos</Title>
              <Text>
                {data?.files?.length} arquivo{data?.files?.length > 1 && 's'} anexado{data?.files?.length > 1 && 's'}
              </Text>
            </div>
          )}

          {!!data?.ticket?.protocolTwilio && (
            <div>
              <Title>Protocolo Twilio</Title>
              <Text>{data?.ticket?.protocolTwilio}</Text>
            </div>
          )}
        </InfoContainer>

        <div>
          <Title>Descrição</Title>
          <Text>
            <RenderRichText bordered html={data?.ticket?.description} />
          </Text>
        </div>

        <ContractList>
          <Button
            variant="outline"
            customColor={themeToggleValue(theme, 'black', {
              dark: theme.colors.element.primary,
            })}
            rounded
            fullWidth
            onClick={() => closeDrawer('ticket-modal')}
            loading={isUpdatingTicket}
            data-testid="cancel-ticket-button"
          >
            Cancelar ticket
          </Button>
          <Button
            customColor={themeToggleValue(theme, 'black', {
              dark: theme.colors.brand.secondaryAlt,
            })}
            rounded
            fullWidth
            onClick={updateOrCreateTicket}
            loading={isUpdatingTicket}
            data-testid={isUpdating ? 'update-ticket-button' : 'open-ticket-button'}
          >
            {isUpdating ? 'Atualizar ticket' : buttonText || 'Abrir ticket'}
          </Button>
        </ContractList>
      </Container>

      <ChildrenDrawer title="Ticket existente" visible={alreadyExistsTicket} container=".ant-drawer-body">
        <Flex gap={8} ph={24} pv={24} direction="column">
          <Flex gap={16} direction="column" mb={16}>
            <DrawerText>
              Já existe um <b>ticket (ID {createdTicket?.ticketId})</b> em andamento com esse mesmo assunto.
            </DrawerText>

            <Alert status="warning">
              Registre um Novo atendimento diretamente nesse ticket para evitar duplicidade.
            </Alert>
          </Flex>

          <Button
            color="black"
            fullWidth
            rounded
            onClick={() => handleRedirectToTicket(createdTicket?.ticketId)}
            data-testid="confirm-ticket-attach-button"
          >
            Ir para o ticket existente
          </Button>
          <Button
            color="black"
            variant="outline"
            rounded
            fullWidth
            data-testid="cancel-ticket-attach-button"
            onClick={handleCancel}
          >
            Fechar
          </Button>
        </Flex>
      </ChildrenDrawer>
    </>
  );
});
